import {
  ArrowLeftOutlined,
  FolderOutlined,
  PictureOutlined
} from "@ant-design/icons"
import { Button, Empty, Skeleton, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { FilePreviewModal } from "../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { getAlbum, getAlbums } from "../../Album/api/album.services.ts"
import { getAttachments } from "../../Album/api/attachment.services.ts"
import { type AlbumListItem } from "../../Album/types/album.types.ts"
import { type Attachment } from "../../Album/types/attachment.types.ts"
import { getFolders } from "../../Files/api/folder.services.ts"
import { documentTypeTagColor } from "../../Files/helpers/document-type-tag-color.ts"
import { folderItemCount } from "../../Files/helpers/folder-item-count.ts"
import { type FolderListItem } from "../../Files/types/folder.types.ts"
import { fileIconFor } from "../../Lead/LeadWorkspacePage/lead-attachment-icon.tsx"
import {
  attachmentName,
  attachmentType,
  visibleLeadAttachments
} from "../../Lead/LeadWorkspacePage/lead-attachments.ts"
import { getLeadDetails } from "../../Lead/api/services.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import type { EntityType } from "../redux/global-map.types.ts"

const { Text } = Typography

// A page big enough to hold every file a real project/lead has today (this
// list has no pagination of its own — it is a preview, not the Files page),
// sorted client-side and sliced to `RECENT_LIMIT` — the same "fetch, sort by
// date, slice" shape `ProjectViewFilesSection`'s own "Recent Files" section
// already uses, rather than adding an `ordering` param to the shared
// `ProjectDocumentsParams` type for this one caller.
const FETCH_LIMIT = 20
const RECENT_LIMIT = 4
const FOLDERS_LIMIT = 3
const ALBUMS_LIMIT = 3

interface QuickViewFileRow {
  key: string
  name: string
  icon: React.ReactNode
  tag: { label: string; color?: string } | null
  // The shape `FilePreviewModal` itself expects — carried per-row so a click
  // can open the exact same preview content (`FilePreviewContent`) the real
  // Files page's `FileViewer` renders, without needing that viewer's full
  // `FileItem`/permissions/actions machinery (rename, move, review, ...),
  // none of which make sense inside a compact quick-view preview.
  doc: { file: string; file_name: string }
}

// Drilling into a folder or album swaps the whole panel body for that
// container's own contents (name + Back), rather than linking out to the
// real Files page — the product ask was to browse it "locally" here. `root`
// is the default landing view (recent files + folders/albums preview).
type QuickViewFilesView =
  | { mode: "root" }
  | { mode: "folder"; id: string; name: string }
  | { mode: "album"; id: string; name: string }

interface QuickViewFilesProps {
  entityId: string
  entityType: EntityType
}

// The map's own URL sync writes `?feature=`/`?layers=` with `replace` (never
// `push` — see `useGlobalMapUrlSync`'s doc comment), so selecting a marker/
// search result never adds a browser-history entry of its own. A plain
// browser Back from the Files page a user reaches through "View all files"
// therefore lands on whatever the LAST pushed `/map` entry was — often the
// bare, no-selection map from the initial page load — not the exact
// record+camera state the user just left. Carrying the map's current URL as
// router `state` lets the Files page offer a real "Back to Location" that
// returns to precisely this state, instead of relying on history.
function currentMapUrl(): string {
  return `${window.location.pathname}${window.location.search}`
}

function attachmentRow(
  attachment: Attachment
): QuickViewFileRow & { date: string } {
  return {
    key: `attachment:${attachment.id}`,
    name: attachment.original_filename,
    icon: getFileIcon(attachment.mime_type),
    tag: attachment.document_type
      ? {
          label: attachment.document_type_label ?? attachment.document_type,
          color: documentTypeTagColor(attachment.document_type)
        }
      : null,
    doc: { file: attachment.file, file_name: attachment.original_filename },
    date: attachment.created
  }
}

/**
 * A small, read-only preview of the record's own Files — reuses the real
 * `core.ProjectAttachment`/`ProjectDocument` (project) or lead-attachment
 * (lead) data this app's full Files pages already show, not a
 * Global-Map-specific store. A project additionally previews its Folders and
 * Albums; pressing one browses its contents locally, right inside this
 * panel (a Back button returns to the root view) rather than navigating away
 * — "View all files" is still there for the real Files page's full
 * rename/move/review tooling. Clicking a file opens the same
 * `FilePreviewContent` the real Files page's `FileViewer` renders, via the
 * lighter, permissions-free `FilePreviewModal` already used for Lead
 * attachments elsewhere in this app.
 */
function QuickViewFiles({ entityId, entityType }: QuickViewFilesProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading")
  const [rows, setRows] = useState<QuickViewFileRow[]>([])
  const [folders, setFolders] = useState<FolderListItem[]>([])
  const [albums, setAlbums] = useState<AlbumListItem[]>([])
  const [previewDoc, setPreviewDoc] = useState<{
    file: string
    file_name: string
  } | null>(null)
  const [view, setView] = useState<QuickViewFilesView>({ mode: "root" })
  const [containerStatus, setContainerStatus] = useState<
    "loading" | "ready" | "error"
  >("loading")
  const [containerRows, setContainerRows] = useState<QuickViewFileRow[]>([])

  // Root-level data — recent files across the whole record, plus the
  // Folders/Albums preview. Reloads only when the selected record changes,
  // never when drilling into a folder/album (that's the effect below).
  useEffect(() => {
    let cancelled = false
    setStatus("loading")
    setRows([])
    setFolders([])
    setAlbums([])
    setView({ mode: "root" })

    const load = async () => {
      if (entityType === "project") {
        // The real Files page (`useFilesData.ts`) merges TWO sources into one
        // list — `core.ProjectAttachment` (photos/screenshots uploaded to an
        // album/folder) and `ProjectDocument` (business documents). This
        // preview used to call only the documents endpoint, so a project
        // whose files are all attachments (the common case — see the real
        // Files page's own "Recent Files": PNGs, not documents) showed "No
        // files yet" here while the real Files page showed several. Fetched
        // in parallel and merged the same way, sorted newest-first.
        const [
          attachmentsResponse,
          documentsResponse,
          foldersResponse,
          albumsResponse
        ] = await Promise.all([
          getAttachments({ project: entityId, page_size: FETCH_LIMIT }),
          getProjectDocuments({ lead: entityId, page_size: FETCH_LIMIT }),
          getFolders({
            project: entityId,
            parent: "root",
            page_size: FOLDERS_LIMIT
          }),
          getAlbums({ project: entityId, page_size: ALBUMS_LIMIT })
        ])
        if (cancelled) return

        const attachmentRows =
          attachmentsResponse.ok && attachmentsResponse.data
            ? (Array.isArray(attachmentsResponse.data)
                ? attachmentsResponse.data
                : attachmentsResponse.data.results
              ).map(attachmentRow)
            : []

        const documentRows: (QuickViewFileRow & { date: string })[] =
          documentsResponse.ok && documentsResponse.data
            ? documentsResponse.data.results.map((document) => ({
                key: `document:${document.id}`,
                name: document.file_name,
                // Documents carry no `mime_type` on this payload — the same
                // gap `mapDocumentToFileItem` already has, so this matches
                // the icon the real Files page itself shows for a document
                // row.
                icon: getFileIcon(undefined),
                tag: document.document_type
                  ? {
                      label:
                        document.document_type_label ?? document.document_type,
                      color: documentTypeTagColor(document.document_type)
                    }
                  : null,
                doc: { file: document.file, file_name: document.file_name },
                date: document.created
              }))
            : []

        // Both file requests failing is the only real error state — one
        // source failing while the other has data still shows what's
        // available, same tolerance the facets fetch already has for a
        // partial failure. Folders/albums are display-only extras here, same
        // as facets — a failure there never blocks the rest of the panel.
        if (!attachmentsResponse.ok && !documentsResponse.ok) {
          setStatus("error")
          return
        }

        const recent = [...attachmentRows, ...documentRows]
          .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
          .slice(0, RECENT_LIMIT)
          .map(({ date: _date, ...row }) => row)
        setRows(recent)
        if (foldersResponse.ok && foldersResponse.data) {
          setFolders(foldersResponse.data.results ?? [])
        }
        if (albumsResponse.ok && albumsResponse.data) {
          setAlbums(albumsResponse.data.results ?? [])
        }
        setStatus("ready")
        return
      }

      const response = await getLeadDetails(entityId)
      if (cancelled) return
      if (!response.ok || !response.data) {
        setStatus("error")
        return
      }
      const recent = visibleLeadAttachments(response.data.attachments)
        .slice()
        .sort((a, b) => (b.created ?? "").localeCompare(a.created ?? ""))
        .slice(0, RECENT_LIMIT)
      setRows(
        recent.map((attachment) => {
          const type = attachmentType(attachment)
          const name = attachmentName(attachment)
          return {
            key: attachment.id,
            name,
            icon: fileIconFor(attachment),
            tag: type ? { label: type } : null,
            doc: { file: attachment.file, file_name: name }
          }
        })
      )
      setStatus("ready")
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [entityId, entityType])

  // A folder's or album's own contents — only ever fetched for a project
  // (Leads render no Folders/Albums section, so `view` never leaves "root"
  // for one). Folder contents merge attachments + documents scoped to that
  // folder, same two sources as the root view; an album's contents are its
  // own `attachments` array, already embedded on the single-album payload.
  useEffect(() => {
    if (view.mode === "root") return
    let cancelled = false
    setContainerStatus("loading")
    setContainerRows([])

    const load = async () => {
      if (view.mode === "folder") {
        const [attachmentsResponse, documentsResponse] = await Promise.all([
          getAttachments({
            project: entityId,
            folder: view.id,
            page_size: FETCH_LIMIT
          }),
          getProjectDocuments({
            lead: entityId,
            folder: view.id,
            page_size: FETCH_LIMIT
          })
        ])
        if (cancelled) return
        if (!attachmentsResponse.ok && !documentsResponse.ok) {
          setContainerStatus("error")
          return
        }
        const attachmentRows =
          attachmentsResponse.ok && attachmentsResponse.data
            ? (Array.isArray(attachmentsResponse.data)
                ? attachmentsResponse.data
                : attachmentsResponse.data.results
              ).map(attachmentRow)
            : []
        const documentRows: (QuickViewFileRow & { date: string })[] =
          documentsResponse.ok && documentsResponse.data
            ? documentsResponse.data.results.map((document) => ({
                key: `document:${document.id}`,
                name: document.file_name,
                icon: getFileIcon(undefined),
                tag: document.document_type
                  ? {
                      label:
                        document.document_type_label ?? document.document_type,
                      color: documentTypeTagColor(document.document_type)
                    }
                  : null,
                doc: { file: document.file, file_name: document.file_name },
                date: document.created
              }))
            : []
        setContainerRows(
          [...attachmentRows, ...documentRows]
            .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
            .map(({ date: _date, ...row }) => row)
        )
        setContainerStatus("ready")
        return
      }

      const response = await getAlbum(view.id)
      if (cancelled) return
      if (!response.ok || !response.data) {
        setContainerStatus("error")
        return
      }
      setContainerRows((response.data.attachments ?? []).map(attachmentRow))
      setContainerStatus("ready")
    }

    void load()
    return () => {
      cancelled = true
    }
    // `entityId` only matters for the folder branch (album contents are
    // self-contained on the album payload) — included anyway since a folder
    // id is never reused across records.
  }, [view, entityId])

  const filesHref =
    entityType === "project"
      ? RoutesPaths.project.view(entityId).files()
      : RoutesPaths.lead.view(entityId, { tab: "files" })

  const hasFoldersOrAlbums = folders.length > 0 || albums.length > 0

  const renderFileRow = (row: QuickViewFileRow) => (
    <li key={row.key} className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        onClick={() => setPreviewDoc(row.doc)}
        className="flex min-w-0 flex-1 items-center gap-2 bg-transparent p-0 text-left"
      >
        <span className="shrink-0 text-gray-400 [&_svg]:text-sm">
          {row.icon}
        </span>
        <Text
          ellipsis={{ tooltip: row.name }}
          className="min-w-0 flex-1 text-sm hover:text-[var(--ant-color-primary)]"
        >
          {row.name}
        </Text>
      </button>
      {row.tag && (
        <Tag color={row.tag.color} className="!m-0 shrink-0">
          {row.tag.label}
        </Tag>
      )}
    </li>
  )

  if (view.mode !== "root") {
    return (
      <div className="flex flex-col gap-3" data-testid="quick-view-files">
        <Button
          type="text"
          size="small"
          aria-label={t("globalMap.quickView.actions.back")}
          icon={<ArrowLeftOutlined />}
          onClick={() => setView({ mode: "root" })}
          className="!h-6 w-fit self-start !px-1 text-xs text-slate-500"
        >
          {t("globalMap.quickView.actions.back")}
        </Button>
        <Text strong className="text-sm">
          {view.name}
        </Text>

        {containerStatus === "loading" && (
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        )}
        {containerStatus === "error" && (
          <Text type="secondary" className="text-xs">
            {t("globalMap.quickView.files.loadError")}
          </Text>
        )}
        {containerStatus === "ready" && containerRows.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("globalMap.quickView.files.empty")}
          />
        )}
        {containerStatus === "ready" && containerRows.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {containerRows.map(renderFileRow)}
          </ul>
        )}

        <FilePreviewModal
          visible={!!previewDoc}
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3" data-testid="quick-view-files">
      {status === "loading" && (
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      )}

      {status === "error" && (
        <Text type="secondary" className="text-xs">
          {t("globalMap.quickView.files.loadError")}
        </Text>
      )}

      {status === "ready" && rows.length === 0 && !hasFoldersOrAlbums && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("globalMap.quickView.files.empty")}
        />
      )}

      {status === "ready" && folders.length > 0 && (
        <div className="flex flex-col gap-1">
          <Text
            type="secondary"
            className="text-xs font-medium uppercase tracking-wide"
          >
            {t("files.sidebar.folders")}
          </Text>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {folders.map((folder) => (
              <li key={folder.id} className="flex min-w-0 items-center gap-2">
                <FolderOutlined className="shrink-0 text-sm text-amber-500" />
                <button
                  type="button"
                  onClick={() =>
                    setView({
                      mode: "folder",
                      id: folder.id,
                      name: folder.name
                    })
                  }
                  className="min-w-0 flex-1 truncate bg-transparent p-0 text-left text-sm text-gray-800 hover:text-[var(--ant-color-primary)]"
                >
                  {folder.name}
                </button>
                <Text type="secondary" className="shrink-0 text-xs">
                  {t("files.folder.itemCount", {
                    count: folderItemCount(folder)
                  })}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status === "ready" && albums.length > 0 && (
        <div className="flex flex-col gap-1">
          <Text
            type="secondary"
            className="text-xs font-medium uppercase tracking-wide"
          >
            {t("files.sidebar.albums")}
          </Text>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {albums.map((album) => (
              <li key={album.id} className="flex min-w-0 items-center gap-2">
                <PictureOutlined className="shrink-0 text-sm text-sky-500" />
                <button
                  type="button"
                  onClick={() =>
                    setView({ mode: "album", id: album.id, name: album.name })
                  }
                  className="min-w-0 flex-1 truncate bg-transparent p-0 text-left text-sm text-gray-800 hover:text-[var(--ant-color-primary)]"
                >
                  {album.name}
                </button>
                <Text type="secondary" className="shrink-0 text-xs">
                  {t("files.albums.itemCount", {
                    count: album.attachments_count
                  })}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status === "ready" && rows.length > 0 && (
        <div className="flex flex-col gap-1">
          {hasFoldersOrAlbums && (
            <Text
              type="secondary"
              className="text-xs font-medium uppercase tracking-wide"
            >
              {t("files.sidebar.all")}
            </Text>
          )}
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map(renderFileRow)}
          </ul>
        </div>
      )}

      <Link
        to={filesHref}
        state={{ fromGlobalMap: currentMapUrl() }}
        className="text-xs font-medium text-[var(--ant-color-primary)]"
      >
        {t("globalMap.quickView.files.viewAll")}
      </Link>

      <FilePreviewModal
        visible={!!previewDoc}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  )
}

export { QuickViewFiles }
